import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { sparkApi } from '@api/spark-client';
import type {
  CreatePromptTopicDto,
  UpdatePromptTopicDto,
  PromptTopicListResponse,
  PromptTopicResponse,
} from '@/types/prompt';

const TOPICS_BASE_URL = '/api/v1/admin/prompt-topics';

export interface GetTopicsParams {
  search?: string;
  limit?: number;
  skip?: number;
}

export interface UpdateTopicParams {
  topicObjectId: string;
  data: UpdatePromptTopicDto;
}

export const topicsQueryKeys = {
  all: ['prompt-topics'] as const,
  lists: () => [...topicsQueryKeys.all, 'list'] as const,
  list: (params: GetTopicsParams) => [...topicsQueryKeys.lists(), params] as const,
  details: () => [...topicsQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...topicsQueryKeys.details(), id] as const,
};

// API functions (internal)
const getTopics = async (params: GetTopicsParams = {}): Promise<PromptTopicListResponse> => {
  const searchParams = new URLSearchParams();

  if (params.search) searchParams.append('search', params.search);
  if (params.limit) searchParams.append('limit', params.limit.toString());
  if (params.skip) searchParams.append('skip', params.skip.toString());

  const url = searchParams.toString()
    ? `${TOPICS_BASE_URL}/?${searchParams.toString()}`
    : `${TOPICS_BASE_URL}/`;

  const response = await sparkApi.get<PromptTopicListResponse>(url);
  return response.data;
};

const createTopic = async (data: CreatePromptTopicDto): Promise<{ message: string }> => {
  const response = await sparkApi.post<{ message: string }>(`${TOPICS_BASE_URL}/`, data);
  return response.data;
};

const getTopic = async (topicObjectId: string): Promise<PromptTopicResponse> => {
  const response = await sparkApi.get<PromptTopicResponse>(`${TOPICS_BASE_URL}/${topicObjectId}`);
  return response.data;
};

const updateTopic = async (params: UpdateTopicParams): Promise<{ message: string }> => {
  const response = await sparkApi.put<{ message: string }>(
    `${TOPICS_BASE_URL}/${params.topicObjectId}`,
    params.data
  );
  return response.data;
};

const deleteTopic = async (topicObjectId: string): Promise<{ message: string }> => {
  const response = await sparkApi.delete<{ message: string }>(
    `${TOPICS_BASE_URL}/${topicObjectId}`
  );
  return response.data;
};

// Query hooks
export function useTopics(params: GetTopicsParams = {}) {
  return useQuery({
    queryKey: topicsQueryKeys.list(params),
    queryFn: () => getTopics(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useTopic(topicObjectId: string, enabled = true) {
  return useQuery({
    queryKey: topicsQueryKeys.detail(topicObjectId),
    queryFn: () => getTopic(topicObjectId),
    enabled: enabled && !!topicObjectId,
    staleTime: 5 * 60 * 1000,
  });
}

// Mutation hooks
export function useCreateTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePromptTopicDto) => createTopic(data),
    onSuccess: () => {
      // Invalidate and refetch topics list
      queryClient.invalidateQueries({ queryKey: topicsQueryKeys.lists() });
      toast.success('Topic created successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to create topic: ${error.response?.data?.detail || error.message}`);
    },
  });
}

export function useUpdateTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdateTopicParams) => updateTopic(params),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: topicsQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: topicsQueryKeys.detail(variables.topicObjectId) });
      toast.success('Topic updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update topic: ${error.response?.data?.detail || error.message}`);
    },
  });
}

export function useDeleteTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (topicObjectId: string) => deleteTopic(topicObjectId),
    onSuccess: (_, variables) => {
      // Invalidate topics list
      queryClient.invalidateQueries({ queryKey: topicsQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: topicsQueryKeys.detail(variables) });
      toast.success('Topic deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete topic: ${error.response?.data?.detail || error.message}`);
    },
  });
}
